import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ChartData } from './interfaces';
import { EChartsOption } from 'echarts/types/dist/shared';

@Injectable()
export class VehicleLeasingChartHelperService {
  private readonly translate = inject(TranslateService);

  getChartOptions(
    months: number,
    maxKm: number,
    chartData: ChartData[]
  ): EChartsOption {
    const xAxisData = this.getXAxisData(months);
    const yAxisData = this.getYAxisData(months, chartData);
    const maxVal = chartData.filter(q => q.km || q.km === 0).length - 1;
    const averageSteps = this.getAverageSteps(months, maxKm);

    return {
      legend: {
        align: 'auto',
      },
      color: ['#808080'],
      xAxis: {
        type: 'category',
        data: xAxisData,
        name: this.translate.instant('Leasing.Month'),
        nameLocation: 'middle',
        nameGap: 40,
      },
      yAxis: {
        type: 'value',
        max: maxKm,
        name: this.translate.instant('Leasing.Km'),
        nameLocation: 'middle',
        nameGap: 70,
      },
      series: [
        {
          color: ['#808080'],
          data: yAxisData,
          type: 'line',
          name: this.translate.instant('Leasing.KmDriven'),
          markLine: {
            symbol: 'none',
            data: [
              [
                {
                  name: this.translate.instant('Leasing.Now'),
                  xAxis: maxVal,
                  yAxis: 0,
                },
                {
                  name: 'end',
                  xAxis: maxVal,
                  yAxis: maxKm,
                },
              ],
            ],
          },
        },
        {
          symbol: 'none',
          name: this.translate.instant('Leasing.IdealKm'),
          data: averageSteps,
          type: 'line',
          color: ['#FFA500'],
          coordinateSystem: 'cartesian2d',
          clip: true,
          lineStyle: {
            type: 'dashed',
          },
        },
      ],
    };
  }

  private getAverageSteps(months: number, maxKm: number): number[] {
    const steps = [0];
    const step = maxKm / months;

    for (let i = 1; i <= months; i++) {
      steps.push(step * i);
    }

    return steps;
  }

  private getXAxisData(months: number): string[] {
    const xAxisData = [];

    for (let i = 1; i <= months; i++) {
      xAxisData.push(i.toString());
    }

    return xAxisData;
  }

  private getYAxisData(months: number, chartData: ChartData[]): number[] {
    const yAxisData: number[] = [];
    let lastVal = 0;

    for (let i = 0; i < months; i++) {
      if (chartData[i].km) {
        const val = Number(chartData[i].km);
        yAxisData.push(val);
        if (lastVal > val) {
          break;
        }
        lastVal = val;
      } else {
        if (lastVal > 0) {
          break;
        }
        yAxisData.push(0);
      }
    }

    return yAxisData;
  }
}
